import { computed, onMounted, onUnmounted, ref } from 'vue'

import router from '@/router'
import { isPublicRoute } from '@/router/utils'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'

const MASK_AFTER_IDLE_MS = 15 * 60 * 1000
const COUNTDOWN_SECONDS = 10 * 60
const DEFAULT_IDLE_TIMEOUT_MINUTES = 60
const ACTIVITY_KEY = 'lastActivityTimestamp'
const MOUSEMOVE_THROTTLE_MS = 5000
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000

/**
 * Where an idle session stands (VH-3676): the screen is masked after 15 idle
 * minutes, the mask adds a logout countdown for the last 10, and the session
 * ends at the server's idle timeout. Pure so the thresholds can be unit tested.
 */
export function idleState(lastActivity: number, now: number, timeoutMs: number) {
  const secondsRemaining = Math.max(0, Math.floor((lastActivity + timeoutMs - now) / 1000))

  return {
    secondsRemaining,
    masked: secondsRemaining > 0 && (now - lastActivity >= MASK_AFTER_IDLE_MS || secondsRemaining <= COUNTDOWN_SECONDS),
    showCountdown: secondsRemaining > 0 && secondsRemaining <= COUNTDOWN_SECONDS,
  }
}

export function useSessionExpiry() {
  const auth = useAuthStore()
  const user = useUserStore()

  const secondsRemaining = ref(Infinity)
  const masked = ref(false)
  const showCountdown = ref(false)

  const isMasked = computed(() => auth.isLoggedIn && masked.value)

  // From /profile, so this runs on the same clock the server enforces. A
  // build-time env var used to set it separately and was never configured, so
  // the client logged users out at 15 minutes while the server allowed 60.
  function idleTimeoutMs(): number {
    return (user.data?.session_idle_timeout_minutes ?? DEFAULT_IDLE_TIMEOUT_MINUTES) * 60 * 1000
  }

  function recordActivity() {
    localStorage.setItem(ACTIVITY_KEY, Date.now().toString())
  }

  // Once masked, only Continue resumes the session — a bumped mouse or a key
  // press must not quietly unmask it. Recompute first: setInterval is
  // suspended while a laptop sleeps, so on wake the first event would
  // otherwise reset the clock before any tick noticed how long it sat idle.
  function onActivity() {
    recompute()
    if (!masked.value)
      recordActivity()
  }

  let lastMousemove = 0

  function onMousemove() {
    const now = Date.now()

    if (now - lastMousemove > MOUSEMOVE_THROTTLE_MS) {
      lastMousemove = now
      onActivity()
    }
  }

  function onVisibility() {
    if (document.visibilityState === 'visible')
      recompute()
  }

  let lastHeartbeatAt = 0

  // The server only slides its own idle-expiry on real API requests, but this
  // countdown resets on any DOM activity. Without a periodic real request, a
  // user who is active on-page but not triggering API calls (e.g. reading a
  // report) can have the server session expire while this countdown still
  // shows time left, causing a surprise 401 logout. Ping the server whenever
  // there's been genuine recent activity so the two clocks stay in sync.
  function maybeSendHeartbeat(lastActivity: number) {
    const now = Date.now()

    if (now - lastHeartbeatAt < HEARTBEAT_INTERVAL_MS)
      return

    if (now - lastActivity > HEARTBEAT_INTERVAL_MS)
      return

    lastHeartbeatAt = now
    user.refreshData().catch(() => undefined)
  }

  function recompute() {
    // Public pages are reachable with no session at all, so neither half of this
    // belongs there: the heartbeat is an authenticated call that 401s on a stale
    // session (which the interceptor turns into a logout + redirect to /login), and
    // the idle timer would log the visitor out from under a page that never needed
    // them signed in. Gated here as well as at the SessionExpiryModal mount site so
    // the behavior doesn't rest on one caller's v-if. (VH-2377)
    if (isPublicRoute(router.currentRoute.value)) {
      reset()

      return
    }

    // Read localStorage directly so logout in another tab is reflected here.
    if (!localStorage.getItem('isAuthenticated')) {
      reset()

      return
    }

    // Shared across tabs, so every open tab masks together and Continue in one
    // unmasks them all.
    const last = Number(localStorage.getItem(ACTIVITY_KEY) || 0)

    if (!last) {
      reset()

      return
    }

    maybeSendHeartbeat(last)

    const state = idleState(last, Date.now(), idleTimeoutMs())

    secondsRemaining.value = state.secondsRemaining
    masked.value = state.masked
    showCountdown.value = state.showCountdown

    if (secondsRemaining.value === 0)
      auth.logout()
  }

  function reset() {
    secondsRemaining.value = Infinity
    masked.value = false
    showCountdown.value = false
  }

  let interval: ReturnType<typeof setInterval> | null = null

  onMounted(() => {
    recordActivity()
    document.addEventListener('keydown', onActivity, { passive: true })
    document.addEventListener('click', onActivity, { passive: true })
    document.addEventListener('touchstart', onActivity, { passive: true })
    document.addEventListener('mousemove', onMousemove, { passive: true })
    document.addEventListener('visibilitychange', onVisibility)
    recompute()
    interval = setInterval(recompute, 1000)
  })

  onUnmounted(() => {
    document.removeEventListener('keydown', onActivity)
    document.removeEventListener('click', onActivity)
    document.removeEventListener('touchstart', onActivity)
    document.removeEventListener('mousemove', onMousemove)
    document.removeEventListener('visibilitychange', onVisibility)
    if (interval)
      clearInterval(interval)
  })

  async function extendSession() {
    await user.refreshData()
    recordActivity()
    recompute()
  }

  return { secondsRemaining, isMasked, showCountdown, extendSession }
}
